import { useUpdateNote } from "@/api/notes/notes";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";

export interface EditNoteTitleModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  noteId: string | null;
  initialTitle: string;
  onUpdated?: () => void;
}

export function EditNoteTitleModal({
  open,
  onOpenChange,
  noteId,
  initialTitle,
  onUpdated,
}: EditNoteTitleModalProps) {
  const { t } = useTranslation();
  const updateNote = useUpdateNote();
  const [title, setTitle] = useState(initialTitle);

  useEffect(() => {
    if (open) setTitle(initialTitle);
  }, [open, initialTitle]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!noteId) return;
    updateNote.mutate(
      { id: noteId, title: title.trim() || t("notes.newNoteTitle") },
      {
        onSuccess: () => {
          onOpenChange(false);
          onUpdated?.();
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{t("notes.editTitleModalTitle")}</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t("notes.newNoteTitle")}
              id="edit-note-title"
              className="w-full"
            />
          </div>
          <DialogFooter className="sm:justify-end gap-2">
            <DialogClose asChild>
              <Button type="button" variant="secondary">
                {t("notes.actionCancel")}
              </Button>
            </DialogClose>
            <Button type="submit" disabled={updateNote.isPending}>
              {t("notes.save")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
